import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, CssBaseline, Link, ThemeProvider, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetMaterialsQuery } from '@/app/services/materials'
import { useGetRatesQuery } from '@/app/services/rates'
import { useGetRateTablesQuery } from '@/app/services/rateTables'
import { useGetSheetsQuery } from '@/app/services/sheets'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import AppBar from '@/common/components/AppBar/AppBar'
import TbxLoadingOverlay from '@/common/components/TbxLoadingOverlay/TbxLoadingOverlay'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'

const classes = {
    layoutContainer: {
        display: 'flex',
        flex: 1,
        padding: 3,
    },

    contentContainer: {
        flexGrow: 1,
        backgroundColor: 'background.default',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
    },
    footer: {
        textAlign: 'center',
        paddingBottom: 2,
        flexGrow: 0,
    },
}

const Layout = ({ children }) => {
    const { t } = useTranslation()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)

    const { isLoading: isLoadingMaterials } = useGetMaterialsQuery({ organisationId })
    const { isLoading: isLoadingSheets } = useGetSheetsQuery({ organisationId })
    const { isLoading: isLoadingCuttingTechs } = useGetCuttingTechnologiesQuery({ organisationId })
    const { isLoading: isLoadingRatesTables } = useGetRateTablesQuery({ organisationId })
    const { isLoading: isLoadingRates } = useGetRatesQuery({ organisationId })

    const theme = useMemo(
        () => (storeSettings ? StoreThemeOverride(lightTheme, storeSettings) : lightTheme),
        [lightTheme, storeSettings]
    )

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppBar />

            <Box
                component="main"
                sx={classes.layoutContainer}
            >
                <Grid
                    component="section"
                    flexGrow={1}
                    spacing={3}
                    sx={classes.contentContainer}
                    container
                >
                    {isLoadingMaterials ||
                    isLoadingSheets ||
                    isLoadingCuttingTechs ||
                    isLoadingRatesTables ||
                    isLoadingRates ? (
                        <TbxLoadingOverlay
                            labelText={t('Loading store settings and materials...')}
                            progressColor="secondary"
                        />
                    ) : (
                        children
                    )}
                </Grid>
            </Box>
            <Grid
                component="footer"
                size={12}
                sx={classes.footer}
            >
                <Typography variant="body2">
                    {t('Powered by') + ' '}
                    <Link
                        color="primary"
                        href={`https://tempustools.com?org_id=${organisationId}&utm_source=quotemate&utm_medium=virality&utm_campaign=web-store`}
                        target="_blank"
                        underline="always"
                    >
                        Tempus Tools
                    </Link>
                </Typography>
            </Grid>
        </ThemeProvider>
    )
}

export default Layout
